import {
  USE_CASES,
  SERVICE_FIELDS,
  EXTRA_FIELDS,
  FIELD_IDS,
  NLU_MODES,
  LATEST_TAG,
  PREFILL_TRIGGER_PARAM,
  PREFILL_TRIGGER_VALUE,
  PREFILL_USE_CASE_PARAM,
  PREFILL_PARAM_KEYS,
  PREFILL_EDIT_PARAM,
  getServicesForUseCase,
  getExtraFieldsForUseCase
} from "./DeployWizard.consts";

const TTL_HOURS_FIELD = "ttlHours";
const POSITIVE_INTEGER_REGEX = /^[1-9]\d*$/;
const TRUE_VALUE = "true";

const TAG_FIELD_IDS = new Set(Object.values(SERVICE_FIELDS).map(({ id }) => id));
const SWITCH_FIELD_IDS = new Set(
  Object.values(EXTRA_FIELDS).filter(({ inputType }) => inputType === "switch").map(({ id }) => id)
);
const NLU_MODE_VALUES = new Set(Object.values(NLU_MODES));

export const getAllowedPrefillFields = (useCaseId, nluMode) => {
  const allowed = new Set([FIELD_IDS.RELEASE_NAME, TTL_HOURS_FIELD]);
  const { required, optional } = getServicesForUseCase(useCaseId);

  [...required, ...optional].forEach(({ id, extraFields, modeSelector }) => {
    allowed.add(id);
    if (!modeSelector || nluMode === NLU_MODES.DEDICATED) {
      extraFields.forEach((field) => allowed.add(field.id));
    }
  });
  getExtraFieldsForUseCase(useCaseId).forEach((field) => allowed.add(field.id));

  if (useCaseId === USE_CASES.ADVANCED.id) {
    allowed.add(FIELD_IDS.NLU_MODE);
  }
  return allowed;
};

const normalizeValue = (fieldId, rawValue) => {
  const value = rawValue.trim();
  if (!value) return undefined;

  if (SWITCH_FIELD_IDS.has(fieldId)) return value === TRUE_VALUE ? true : undefined;
  if (TAG_FIELD_IDS.has(fieldId)) return value === LATEST_TAG ? value : value.toLowerCase();
  if (fieldId === FIELD_IDS.RELEASE_NAME) return value.replace(/ /g, "-");
  if (fieldId === TTL_HOURS_FIELD) return POSITIVE_INTEGER_REGEX.test(value) ? value : undefined;
  if (fieldId === FIELD_IDS.NLU_MODE) return NLU_MODE_VALUES.has(value) ? value : undefined;
  return value;
};

const pickFields = (searchParams, allowed) => {
  const data = {};
  allowed.forEach((fieldId) => {
    const rawValue = searchParams.get(fieldId);
    if (rawValue === null) return;
    const value = normalizeValue(fieldId, rawValue);
    if (value !== undefined) data[fieldId] = value;
  });
  return data;
};

export const parsePrefillParams = (searchParams) => {
  if (searchParams.get(PREFILL_TRIGGER_PARAM) !== PREFILL_TRIGGER_VALUE) return null;

  const requestedUseCase = searchParams.get(PREFILL_USE_CASE_PARAM);
  const useCaseId = USE_CASES[requestedUseCase] ? requestedUseCase : null;
  const nluMode = normalizeValue(FIELD_IDS.NLU_MODE, searchParams.get(FIELD_IDS.NLU_MODE) ?? "");
  const allowed = useCaseId
    ? getAllowedPrefillFields(useCaseId, nluMode)
    : new Set([FIELD_IDS.RELEASE_NAME, TTL_HOURS_FIELD]);

  return {
    ...(useCaseId && { [FIELD_IDS.USE_CASE]: useCaseId }),
    ...pickFields(searchParams, allowed)
  };
};

export const getEditReleaseName = (searchParams) => searchParams.get(PREFILL_EDIT_PARAM)?.trim() || null;

const getRowNluMode = (row) => (row.nluRuntimeModes?.includes(NLU_MODES.DEDICATED) ? NLU_MODES.DEDICATED : NLU_MODES.API);

export const buildEditPrefill = (row, searchParams) => {
  const useCaseId = USE_CASES[row.useCase] ? row.useCase : USE_CASES.ADVANCED.id;
  const nluMode = normalizeValue(FIELD_IDS.NLU_MODE, searchParams.get(FIELD_IDS.NLU_MODE) ?? "") ?? getRowNluMode(row);
  const allowed = getAllowedPrefillFields(useCaseId, nluMode);
  allowed.delete(FIELD_IDS.RELEASE_NAME);

  return { ...row, ...pickFields(searchParams, allowed) };
};

export const removePrefillParams = (searchParams) => {
  const next = new URLSearchParams(searchParams);
  PREFILL_PARAM_KEYS.forEach((key) => next.delete(key));
  return next;
};
